import { Injectable, inject } from '@angular/core';
import { Apollo, QueryRef, gql } from 'apollo-angular';
import { Observable, catchError, map, of } from 'rxjs';
import { Appointment, PageRequestInput } from 'src/app/_graphql/schema';
import { AuthService } from 'src/app/_services/auth.service';
import { UiService } from 'src/app/_services/ui.service';

@Injectable({
  providedIn: 'root'
})
export class WaitingListService {

  apollo = inject(Apollo);
  auth = inject(AuthService);
  ui = inject(UiService);

  public queryParams: PageRequestInput = {
    showDeleted: false,
    query: '',
    sortBy: '',
    sortOrder: '',
    skip: 0,
    take: 25,
  };
  public totalCount = 0;
  public pageIndex = 0;
  qRef!: QueryRef<Appointment[]>;
  qRefChanges!: Observable<Appointment[]>;

  waitingListFilters: WaitingListFilters = {
    companyId: this.auth.user.companyId,
    appointmentTypeId: null,
    unitId: null,
    includeInitiated: false,
    includeScheduled: false,
    startDate: null,
    endDate: null,
  }

  protected waitingListQuery = gql`
    query waitingList($companyId:ID $unitId:ID $doctorId:ID $appointmentTypeId:ID $startDate:DateOnly $endDate:DateOnly $includeScheduled:Boolean $includeInitiated:Boolean, $pageRequestInput:PageRequestInput){
      waitingListAppointments(
          companyId:$companyId
          unitId:$unitId
          doctorId:$doctorId
          appointmentTypeId:$appointmentTypeId
          startDate:$startDate
          endDate:$endDate
          includeScheduled:$includeScheduled,
          includeInitiated:$includeInitiated,
          pageRequestInput:$pageRequestInput
      ){
        totalCount
        data{
          id
          created
          patientId
          priority
          status
          expectedAppointmentTime
          patient{
            id
            firstName
            lastName
            uIDN
          }
          appointedToDoctor{
            id
            firstName
            lastName
          }
          appointedToUnit{
            id
            name
          }
          appointmentType{
            id name
          }
        }
      }
    }
  `;

  public getWaitingList(data: WaitingListFilters): Observable<Appointment[]> {
    this.ui.loading = true;
    if (!this.qRef) {
      this.qRef = this.apollo.watchQuery<Appointment[]>({
        query: this.waitingListQuery,
        fetchPolicy: 'cache-first',
        variables: { pageRequestInput: { ...this.queryParams }, ...data }
      });

      this.qRefChanges = this.qRef.valueChanges.pipe(
        map((result: any) => {
          this.ui.loading = false;
          if (!result || !result.data)
            return of([]);

          const keys = Object.keys(result.data);
          if (result.data && keys.length) {
            this.totalCount = result.data[keys[0]].totalCount || 0;
            return result.data[keys[0]].data;
          }

          return of([]);
        }),
        catchError(error => {
          this.ui.openSnackBarError(error.message);
          throw error;
        }),
      ) as any;

      return this.qRefChanges;
    }

    this.fetchMoreData(data);

    return this.qRefChanges;
  }

  public applyPager(pageIndex: number, pageSize: number, data: WaitingListFilters) {
    this.pageIndex = pageIndex;
    this.queryParams.take = pageSize;
    this.queryParams.skip = pageIndex * pageSize;
    this.fetchMoreData(data);
  }

  public fetchMoreData(variables: any) {
    if (this.qRef)
      this.qRef.fetchMore({
        variables: { pageRequestInput: this.queryParams, ...variables }
      });
    else
      console.log("Query is not initialized!");
  }

  public refetchWaitingList() {
    this.qRef?.refetch(this.waitingListFilters);
  }
}

export interface WaitingListFilters {
  companyId: string;
  unitId?: string | null;
  appointmentTypeId?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
  includeScheduled?: boolean;
  includeInitiated?: boolean;
}
